import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RecentOrderDto } from '../../api-types/models/OrderDto';
import { OrderStatisticsDto } from '../../api-types/models/OrderStatisticsDto';
import { getWsToken } from '../../api/auth';
import { FoodIcon } from '../../components/foodIcon';
import { StatsItem } from './StatsItem';
import { isEmptyStatistics } from '../../utils/isEmptyStatistics';
import { parseObserverParams } from './utils';
import { useObserverWebSocket } from './useObserverWebSocket';

import './index.css';

export function ObserverPage(): React.ReactNode {
    const location = useLocation();
    const { theme, refreshIntervalMs, snapshotMode } = useMemo(
        () => parseObserverParams(location.search),
        [location.search]
    );

    const [token, setToken] = useState<string>();
    const [reconnectKey, setReconnectKey] = useState(0);
    const [orders, setOrders] = useState<RecentOrderDto[]>([]);
    const [statistics, setStatistics] = useState<OrderStatisticsDto>();
    const [isKitchenOpen, setIsKitchenOpen] = useState<boolean>(false);

    // Fetch initial token
    useEffect(() => {
        getWsToken().then((res) => setToken(res.data.token));
    }, []);

    // Handle state updates from WebSocket hook
    const handleStateUpdate = useCallback(
        (updates: {
            orders?: RecentOrderDto[];
            statistics?: OrderStatisticsDto;
            isKitchenOpen?: boolean;
        }) => {
            if (updates.orders !== undefined) setOrders(updates.orders);
            if (updates.statistics !== undefined)
                setStatistics(updates.statistics);
            if (updates.isKitchenOpen !== undefined)
                setIsKitchenOpen(updates.isKitchenOpen);
        },
        []
    );

    // Trigger reconnect when token fetch fails
    useEffect(() => {
        if (token === undefined) return;
        const timeout = setTimeout(() => setReconnectKey((k) => k + 1), 0);
        return () => clearTimeout(timeout);
    }, [token]);

    // Manage WebSocket connection
    useObserverWebSocket(
        token,
        reconnectKey,
        refreshIntervalMs,
        snapshotMode,
        handleStateUpdate
    );

    const pageClassName = `observer-page ${theme} eink`;

    if (!isKitchenOpen) {
        return (
            <div className={pageClassName} id='observer'>
                <div className={`no-orders observer ${theme}`}>
                    <img src='/images/kitchen_closed.png' alt='closed' />
                    <div className='dish'>
                        <h4>BAR:</h4>
                        <h3>7:00 - 22:00</h3>
                    </div>
                    <div className='dish'>
                        <h4>BREAKFAST:</h4>
                        <h3>7:00 - 10:30</h3>
                    </div>
                    <div className='dish'>
                        <h4>LUNCH:</h4>
                        <h3>11:00 - 14:00</h3>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={pageClassName} id='observer'>
            {orders.length ? (
                orders.map((order: RecentOrderDto) => (
                    <div className='observer-item' key={order.orderItemId}>
                        <div className='graphics'>
                            <FoodIcon
                                foodType={order.menuItem.icon}
                                color='white'
                            />
                            {order.servingLine}
                        </div>
                        <strong className='order-title'>
                            {order.displayName}
                        </strong>
                    </div>
                ))
            ) : (
                <div className='empty-orders'>
                    <img src='/images/cooking.png' alt='cooking' />
                    <h3>The kitchen is ready, and there's no one in line.</h3>
                </div>
            )}
            {statistics && !isEmptyStatistics(statistics) && (
                <div className='stats'>
                    <StatsItem title='served' value={statistics.completed} />
                </div>
            )}
        </div>
    );
}
